import {
  lazy,
  Suspense,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode
} from "react";
import type { editor as MonacoEditor } from "monaco-editor";
import type {
  FindingSeverity,
  RiskTier,
  ReviewRunState,
  SpecialistRole
} from "@workerstation/shared";
import type { CheckTerminalState } from "@workerstation/shared";
import type { FindingLifecycleState } from "@workerstation/shared";
import type {
  ReviewDeepLinkState,
  ReviewSection
} from "@workerstation/shared";
import { reviewStateForSection } from "./review-tab-model";
import {
  ReviewCommandPalette,
  toSurfaceCommands,
  type ReviewCommandItem
} from "./ReviewCommandPalette.tsx";
import { useSurfaceCommands } from "./commands/react.tsx";
import { LineCommentThread, type LineCommentData } from "./LineComment.tsx";
import {
  REVIEW_COMMAND_DEFINITIONS,
  REVIEW_PALETTE_ARIA_KEY_SHORTCUTS,
  REVIEW_WORKBENCH_ARIA_KEY_SHORTCUTS,
  adjacentReviewIdentity,
  reviewCommandFromShortcut,
  type ReviewCommandId
} from "./review-command-model";
import {
  groupReviewChangedFiles,
  reviewAssertiveAnnouncement,
  reviewCandidatePresentation,
  reviewFileDiffForSelection,
  reviewMilestoneAnnouncement,
  reviewRovingIndex,
  reviewRouteIntegrity,
  reviewStatusPresentation,
  type ReviewCandidatePresentation,
  type ReviewChangedFileInput,
  type ReviewFileDiff,
  type ReviewFileDiffInput,
  type ReviewFileGroup
} from "./review-workbench-model";

const CodeDiffEditor = lazy(async () => ({
  default: (await import("./tabs/code-diff-editor.tsx")).CodeDiffEditor
}));

const SECTIONS: readonly Readonly<{
  id: ReviewSection;
  label: string;
}>[] = [
  { id: "summary", label: "Summary" },
  { id: "changes", label: "Changes" },
  { id: "conflicts", label: "Conflicts" },
  { id: "findings", label: "Findings" },
  { id: "checks", label: "Checks" },
  { id: "reviews", label: "Reviews" },
  { id: "activity", label: "Activity" }
];

export type ReviewHunkDirection = "next" | "previous";

function reviewShortcutTargetIsEditable(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return (
    target.closest(
      "input, textarea, select, [contenteditable]:not([contenteditable='false']), [role='textbox'], .monaco-editor"
    ) !== null
  );
}

function moveTreeFocus(event: KeyboardEvent<HTMLElement>): void {
  if (event.altKey || event.ctrlKey || event.metaKey) return;
  const tree = event.currentTarget.closest('[role="tree"]');
  if (!tree) return;
  const items = [
    ...tree.querySelectorAll<HTMLElement>(
      '[data-review-tree-item]:not([aria-disabled="true"])'
    )
  ];
  const current = items.indexOf(event.currentTarget);
  if (current < 0) return;
  const expanded = event.currentTarget.getAttribute("aria-expanded");
  if (
    (event.key === "ArrowRight" && expanded === "false") ||
    (event.key === "ArrowLeft" && expanded === "true")
  ) {
    event.preventDefault();
    event.currentTarget.click();
    return;
  }
  const next = reviewRovingIndex(
    event.key,
    current,
    items.length,
    "vertical"
  );
  if (next === undefined) return;
  event.preventDefault();
  items.forEach((item, index) => {
    item.tabIndex = index === next ? 0 : -1;
  });
  items[next]?.focus();
}

export interface ReviewSummaryData {
  readonly baseCommitId: string;
  readonly targetRef: string;
  readonly targetCommitId: string;
  readonly targetRevision: number;
  readonly candidateCommitId: string;
  readonly candidateTreeId: string;
  readonly freshness:
    | "current"
    | "target-moved"
    | "evidence-stale"
    | "authority-stale";
  readonly freshnessReasons: readonly string[];
  readonly riskTier: RiskTier;
  readonly riskReasons: readonly string[];
  readonly blockers: readonly Readonly<{
    id: string;
    label: string;
    section: ReviewSection;
  }>[];
  readonly requiredChecks: Readonly<{
    passed: number;
    total: number;
  }>;
  readonly coverage: Readonly<{
    reviewedFiles: number;
    totalFiles: number;
    completedSpecialists: number;
    requiredSpecialists: number;
  }>;
  readonly gate: Readonly<{
    label: string;
    enabled: boolean;
    reason?: string;
  }>;
}

export interface ReviewFindingItem {
  readonly findingId: string;
  readonly severity: FindingSeverity;
  readonly title: string;
  readonly state: FindingLifecycleState;
  readonly workspaceId?: string;
  readonly path?: string;
  readonly specialist: string;
  readonly detail?: string;
}

export interface ReviewCheckItem {
  readonly checkId: string;
  readonly label: string;
  readonly state: "queued" | "running" | CheckTerminalState;
  readonly durationMs?: number;
  readonly retryable?: boolean;
  readonly detail?: string;
}

export interface ReviewSpecialistItem {
  readonly reviewId: string;
  readonly role: SpecialistRole;
  readonly state: ReviewRunState;
  readonly required: boolean;
  readonly attempt: number;
  readonly parentReviewId?: string;
  readonly files: readonly string[];
  readonly domains: readonly string[];
  readonly latestMilestone?: string;
  readonly partialFindingCount?: number;
  readonly retryable?: boolean;
}

export interface ReviewActivityItem {
  readonly eventId: string;
  readonly sequence: number;
  readonly label: string;
  readonly detail?: string;
  readonly createdAt: number;
}

export interface ReviewWorkbenchData {
  readonly changeId: string;
  readonly candidate: ReviewCandidatePresentation;
  readonly summary?: ReviewSummaryData;
  readonly files?: readonly ReviewChangedFileInput[];
  /** Lazy payload for the exact file selected by the immutable review route. */
  readonly diff?: ReviewFileDiffInput;
  readonly findings?: readonly ReviewFindingItem[];
  readonly checks?: readonly ReviewCheckItem[];
  readonly specialists?: readonly ReviewSpecialistItem[];
  readonly activity?: readonly ReviewActivityItem[];
}

interface ReviewWorkbenchProps {
  state: ReviewDeepLinkState;
  onNavigate: (state: ReviewDeepLinkState) => void;
  data?: ReviewWorkbenchData;
  onGateAction?: () => void;
  onNavigateHunk?: (direction: ReviewHunkDirection) => void;
  onMarkFileReviewed?: (file: Readonly<{
    workspaceId: string;
    path: string;
  }>) => void;
  onRetryCheck?: (checkId: string) => void;
  onRetrySpecialist?: (reviewId: string) => void;
}

function Metric({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 rounded border border-kumo-line bg-kumo-base p-3">
      <dt className="text-[11px] font-medium uppercase tracking-wide text-kumo-subtle">
        {label}
      </dt>
      <dd className="mt-1 break-all text-xs text-kumo-default">{children}</dd>
    </div>
  );
}

function EmptySection({ children }: { children: ReactNode }) {
  return (
    <p className="rounded border border-dashed border-kumo-line p-5 text-center text-xs leading-5 text-kumo-subtle">
      {children}
    </p>
  );
}

function StatusText({
  status,
  label,
  className = ""
}: {
  status: string;
  label?: string;
  className?: string;
}) {
  const presentation = reviewStatusPresentation(status, label);
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <span aria-hidden="true">{presentation.symbol}</span>
      <span>{presentation.label}</span>
    </span>
  );
}

function cleanCandidateState(
  state: ReviewDeepLinkState,
  candidateId: string,
  section: ReviewSection,
  comparison = state.comparison
): ReviewDeepLinkState {
  return {
    version: state.version,
    ...(state.changeId === undefined ? {} : { changeId: state.changeId }),
    candidateId: candidateId as ReviewDeepLinkState["candidateId"],
    section,
    comparison
  };
}

function CandidateStatus({
  candidate,
  state,
  onNavigate
}: {
  candidate: ReviewCandidatePresentation;
  state: ReviewDeepLinkState;
  onNavigate: (state: ReviewDeepLinkState) => void;
}) {
  if (candidate.state === "current") return null;
  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-xs"
    >
      <p className="text-kumo-default">
        <StatusText
          status="superseded"
          label={`Historical candidate generation ${candidate.generation}.`}
        />{" "}
        This immutable view is read-only and has not been redirected. Superseded by{" "}
        <code>{candidate.supersededByCandidateId}</code>; current candidate is{" "}
        <code>{candidate.currentCandidateId}</code>.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() =>
            onNavigate(
              cleanCandidateState(
                state,
                candidate.candidateId,
                state.section,
                "merge-impact"
              )
            )
          }
          className="rounded border border-kumo-line px-2 py-1 text-kumo-default hover:bg-kumo-elevated"
        >
          Inspect merge impact
        </button>
        <button
          type="button"
          onClick={() =>
            onNavigate(
              cleanCandidateState(
                state,
                candidate.currentCandidateId,
                "summary"
              )
            )
          }
          className="rounded bg-kumo-brand px-2 py-1 text-white"
        >
          Open current
        </button>
      </div>
    </div>
  );
}

function SummarySection({
  summary,
  state,
  onNavigate,
  onGateAction,
  historical
}: {
  summary?: ReviewSummaryData;
  state: ReviewDeepLinkState;
  onNavigate: (state: ReviewDeepLinkState) => void;
  onGateAction?: () => void;
  historical: boolean;
}) {
  const freshnessHeadingId = useId();
  const riskHeadingId = useId();
  const blockersHeadingId = useId();
  if (!summary) {
    return <EmptySection>Candidate summary data is not available yet.</EmptySection>;
  }
  const freshnessLabel = {
    current: "Target current",
    "target-moved": "Target moved",
    "evidence-stale": "Evidence stale",
    "authority-stale": "Authority changed"
  }[summary.freshness];
  const gateMessage = historical
    ? "Historical candidates are immutable and cannot be published."
    : summary.freshness !== "current"
      ? "Candidate freshness must be restored before publication."
      : summary.gate.enabled
        ? "All current gate conditions are satisfied."
        : summary.gate.reason ?? "Publication is currently blocked.";
  const gateStatus = historical
    ? "superseded"
    : summary.freshness !== "current"
      ? summary.freshness
      : summary.gate.enabled
        ? "passed"
        : "blocked";
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-kumo-default">
            Candidate readiness
          </h2>
          <p className="mt-1 text-xs text-kumo-subtle">
            Exact immutable inputs and the current publication gate.
          </p>
        </div>
        <StatusText
          status={summary.freshness}
          label={freshnessLabel}
          className={`rounded-full border px-2 py-1 text-[11px] font-medium ${
            summary.freshness === "current"
              ? "border-emerald-500/40 text-emerald-600"
              : "border-amber-500/40 text-amber-600"
          }`}
        />
      </div>

      <dl className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        <Metric label="Base commit">
          <code>{summary.baseCommitId}</code>
        </Metric>
        <Metric label="Target">
          <code>
            {summary.targetRef}@{summary.targetRevision} · {summary.targetCommitId}
          </code>
        </Metric>
        <Metric label="Candidate commit">
          <code>{summary.candidateCommitId}</code>
        </Metric>
        <Metric label="Candidate tree">
          <code>{summary.candidateTreeId}</code>
        </Metric>
        <Metric label="Risk tier">
          <StatusText status={summary.riskTier} />
        </Metric>
        <Metric label="Required checks">
          {summary.requiredChecks.passed}/{summary.requiredChecks.total} passed
        </Metric>
        <Metric label="Review coverage">
          {summary.coverage.reviewedFiles}/{summary.coverage.totalFiles} files ·{" "}
          {summary.coverage.completedSpecialists}/
          {summary.coverage.requiredSpecialists} specialists
        </Metric>
      </dl>

      {summary.freshness !== "current" && (
        <section
          aria-labelledby={freshnessHeadingId}
          className="rounded border border-amber-500/40 bg-amber-500/5 p-3"
        >
          <h3
            id={freshnessHeadingId}
            className="text-xs font-semibold text-kumo-default"
          >
            Freshness requires attention
          </h3>
          {summary.freshnessReasons.length === 0 ? (
            <p className="mt-2 text-xs text-kumo-subtle">
              The candidate no longer matches current publication inputs.
            </p>
          ) : (
            <ul className="mt-2 space-y-1 text-xs text-kumo-subtle">
              {summary.freshnessReasons.map((reason) => (
                <li key={reason}>• {reason}</li>
              ))}
            </ul>
          )}
        </section>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        <section aria-labelledby={riskHeadingId} className="rounded border border-kumo-line p-3">
          <h3 id={riskHeadingId} className="text-xs font-semibold text-kumo-default">
            Why this risk level
          </h3>
          {summary.riskReasons.length === 0 ? (
            <p className="mt-2 text-xs text-kumo-subtle">No elevated risk reasons.</p>
          ) : (
            <ul className="mt-2 space-y-1 text-xs text-kumo-subtle">
              {summary.riskReasons.map((reason) => (
                <li key={reason}>• {reason}</li>
              ))}
            </ul>
          )}
        </section>
        <section
          data-review-blocker-report=""
          tabIndex={-1}
          aria-labelledby={blockersHeadingId}
          className="rounded border border-kumo-line p-3"
        >
          <h3 id={blockersHeadingId} className="text-xs font-semibold text-kumo-default">
            Blockers
          </h3>
          {summary.blockers.length === 0 ? (
            <p className="mt-2 text-xs text-emerald-600">No active blockers.</p>
          ) : (
            <ul className="mt-2 space-y-1">
              {summary.blockers.map((blocker) => (
                <li key={blocker.id}>
                  <button
                    type="button"
                    className="text-left text-xs text-kumo-brand hover:underline"
                    onClick={() =>
                      onNavigate(reviewStateForSection(state, blocker.section))
                    }
                  >
                    {blocker.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 rounded border border-kumo-line bg-kumo-elevated p-3 shadow-sm">
        <p className="text-xs text-kumo-subtle">
          <StatusText status={gateStatus} label={gateMessage} />
        </p>
        <button
          type="button"
          disabled={
            historical ||
            (summary.gate.enabled && summary.freshness === "current"
              ? onGateAction === undefined
              : summary.blockers.length === 0)
          }
          onClick={
            summary.gate.enabled && summary.freshness === "current"
              ? onGateAction
              : (event) => {
                  const report = event.currentTarget
                    .closest("[aria-label='Change review workbench']")
                    ?.querySelector<HTMLElement>("[data-review-blocker-report]");
                  report?.focus();
                  report?.scrollIntoView({ block: "nearest" });
                }
          }
          className="rounded bg-kumo-brand px-3 py-2 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-40"
        >
          {historical
            ? "Historical candidate"
            : summary.gate.enabled && summary.freshness === "current"
              ? summary.gate.label
              : summary.blockers.length > 0
                ? "Inspect blockers"
                : "Refresh required"}
        </button>
      </div>
    </div>
  );
}

function FileGroups({
  title,
  groups,
  state,
  onNavigate
}: {
  title: string;
  groups: readonly ReviewFileGroup[];
  state: ReviewDeepLinkState;
  onNavigate: (state: ReviewDeepLinkState) => void;
}) {
  return (
    <section aria-label={title}>
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-kumo-subtle">
        {title}
      </h3>
      <div className="space-y-2">
        {groups.map((group) => {
          const selectedIndex = group.files.findIndex(
            (file) =>
              state.workspaceId === file.workspaceId && state.file === file.path
          );
          return (
          <details key={group.id} className="rounded border border-kumo-line">
            <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-kumo-default">
              {group.label} <span className="text-kumo-subtle">({group.files.length})</span>
            </summary>
            {group.files.length > 0 && (
              <ul
                className="border-t border-kumo-line py-1"
                role="tree"
                aria-label={`${group.label} files`}
              >
                {group.files.map((file, index) => {
                  const selected =
                    state.workspaceId === file.workspaceId && state.file === file.path;
                  return (
                    <li key={`${file.workspaceId}:${file.path}`} role="none">
                      <button
                        type="button"
                        role="treeitem"
                        aria-level={1}
                        aria-posinset={index + 1}
                        aria-setsize={group.files.length}
                        aria-selected={selected}
                        data-review-tree-item=""
                        tabIndex={selected || (selectedIndex < 0 && index === 0) ? 0 : -1}
                        onKeyDown={moveTreeFocus}
                        onClick={() =>
                          onNavigate({
                            version: state.version,
                            ...(state.changeId === undefined ? {} : { changeId: state.changeId }),
                            ...(state.candidateId === undefined ? {} : { candidateId: state.candidateId }),
                            workspaceId: file.workspaceId,
                            section: "changes",
                            comparison: state.comparison,
                            file: file.path
                          })
                        }
                        className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs ${
                          selected ? "bg-kumo-brand/10 text-kumo-default" : "text-kumo-subtle hover:bg-kumo-base"
                        }`}
                      >
                        <StatusText
                          status={file.changeKind}
                          label={`${file.changeKind} file`}
                          className="shrink-0 font-mono uppercase"
                        />
                        <span className="min-w-0 flex-1 truncate font-mono">{file.path}</span>
                        {file.conflictCount > 0 && (
                          <StatusText
                            status="conflict"
                            label={`${file.conflictCount} conflicts`}
                            className="text-amber-600"
                          />
                        )}
                        {(file.contributingTaskIds.length > 0 ||
                          file.contributingRunIds.length > 0) && (
                          <span
                            className="text-kumo-subtle"
                            aria-label={`${file.contributingTaskIds.length} contributing tasks and ${file.contributingRunIds.length} contributing runs`}
                          >
                            {file.contributingTaskIds.length} tasks ·{" "}
                            {file.contributingRunIds.length} runs
                          </span>
                        )}
                        {!file.binary && (file.additions > 0 || file.deletions > 0) && (
                          <span aria-label={`${file.additions} additions and ${file.deletions} deletions`}>
                            <span className="text-emerald-600">+{file.additions}</span>{" "}
                            <span className="text-red-600">−{file.deletions}</span>
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </details>
          );
        })}
      </div>
    </section>
  );
}

function ChangesSection({
  files = [],
  state,
  onNavigate,
  candidateId,
  diff,
  diffRejected,
  diffMode,
  onDiffModeChange,
  onDiffMount
}: {
  files?: readonly ReviewChangedFileInput[];
  state: ReviewDeepLinkState;
  onNavigate: (state: ReviewDeepLinkState) => void;
  candidateId?: string;
  diff?: ReviewFileDiff;
  diffRejected: boolean;
  diffMode: "unified" | "side-by-side";
  onDiffModeChange: (mode: "unified" | "side-by-side") => void;
  onDiffMount: (editor: MonacoEditor.IStandaloneDiffEditor) => void;
}) {
  const integratedDiffHeadingId = useId();
  const groups = groupReviewChangedFiles(files);
  if (groups.total === 0) {
    return <EmptySection>No changed files are available for this candidate.</EmptySection>;
  }
  return (
    <div className="space-y-5">
      <div className="grid gap-5 lg:grid-cols-2">
        <FileGroups title="Review state" groups={groups.attention} state={state} onNavigate={onNavigate} />
        <FileGroups title="File kind" groups={groups.kind} state={state} onNavigate={onNavigate} />
      </div>
      {diffRejected && (
        <div
          role="alert"
          className="rounded border border-red-500/40 bg-red-500/5 p-3 text-xs text-kumo-default"
        >
          Immutable diff payload rejected because it does not match the selected
          candidate, commit, tree, workspace, or file.
        </div>
      )}
      {diff && candidateId && (
        <section
          aria-labelledby={integratedDiffHeadingId}
          className="overflow-hidden rounded border border-kumo-line"
        >
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-kumo-line px-3 py-2">
            <div>
              <h3
                id={integratedDiffHeadingId}
                className="text-xs font-semibold text-kumo-default"
              >
                Integrated immutable diff
              </h3>
              <p className="mt-1 font-mono text-[11px] text-kumo-subtle">
                {diff.modified.workspaceId}/{diff.modified.path}
              </p>
            </div>
            <div
              role="group"
              aria-label="Diff layout"
              className="flex rounded border border-kumo-line p-0.5"
            >
              {(["side-by-side", "unified"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={diffMode === mode}
                  onClick={() => onDiffModeChange(mode)}
                  className={`rounded px-2 py-1 text-[11px] ${
                    diffMode === mode
                      ? "bg-kumo-brand text-white"
                      : "text-kumo-subtle hover:bg-kumo-base"
                  }`}
                >
                  {mode === "side-by-side" ? "Side by side" : "Unified"}
                </button>
              ))}
            </div>
          </div>
          <div className="h-[min(62vh,760px)] min-h-[380px]">
            <Suspense
              fallback={
                <div
                  role="status"
                  className="flex h-full items-center justify-center text-xs text-kumo-subtle"
                >
                  Loading immutable diff…
                </div>
              }
            >
              <CodeDiffEditor
                candidateId={candidateId}
                original={diff.original}
                modified={diff.modified}
                mode={diffMode}
                onMount={onDiffMount}
              />
            </Suspense>
          </div>
          <div className="border-t border-kumo-line p-2">
            <LineCommentThread
              filePath={diff.modified.path}
              lineNumber={1}
              comments={[]}
              onAddComment={(path, line, text) => {
                console.log("Add line comment", path, line, text);
              }}
            />
          </div>
        </section>
      )}
    </div>
  );
}

function ConflictsSection({
  files = [],
  state,
  onNavigate
}: {
  files?: readonly ReviewChangedFileInput[];
  state: ReviewDeepLinkState;
  onNavigate: (state: ReviewDeepLinkState) => void;
}) {
  const conflicts = groupReviewChangedFiles(files).attention[0]!;
  if (conflicts.files.length === 0) {
    return <EmptySection>No source conflicts are recorded for this candidate.</EmptySection>;
  }
  return (
    <FileGroups
      title="Conflicts requiring resolution"
      groups={[conflicts]}
      state={state}
      onNavigate={onNavigate}
    />
  );
}

function FindingTreeItem({
  finding,
  index,
  size,
  selected,
  tabStop,
  state,
  onNavigate
}: {
  finding: ReviewFindingItem;
  index: number;
  size: number;
  selected: boolean;
  tabStop: boolean;
  state: ReviewDeepLinkState;
  onNavigate: (state: ReviewDeepLinkState) => void;
}) {
  const detailId = useId();
  const [expanded, setExpanded] = useState(selected);
  useEffect(() => {
    if (selected) setExpanded(true);
  }, [selected]);
  return (
    <li role="none">
      <button
        type="button"
        role="treeitem"
        aria-level={1}
        aria-posinset={index + 1}
        aria-setsize={size}
        aria-selected={selected}
        aria-expanded={expanded}
        aria-controls={detailId}
        data-review-tree-item=""
        tabIndex={tabStop ? 0 : -1}
        onKeyDown={moveTreeFocus}
        onClick={() => {
          setExpanded((current) => !current);
          onNavigate({
            ...state,
            findingId:
              finding.findingId as ReviewDeepLinkState["findingId"]
          });
        }}
        className={`flex w-full items-start gap-2 rounded px-3 py-2 text-left text-xs text-kumo-default ${
          selected
            ? "bg-kumo-brand/10 outline outline-1 outline-kumo-brand/30"
            : "hover:bg-kumo-base"
        }`}
      >
        <span aria-hidden="true" className="mt-px shrink-0">
          {expanded ? "▾" : "▸"}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-semibold">{finding.title}</span>
          <span className="mt-1 flex flex-wrap gap-x-2 text-[11px] uppercase text-kumo-subtle">
            <StatusText status={finding.severity} />
            <StatusText status={finding.state} />
          </span>
        </span>
      </button>
      {expanded && (
        <div
          id={detailId}
          role="group"
          aria-label={`${finding.title} details`}
          className="mx-3 mb-3 space-y-1 border-t border-kumo-line pt-2 text-[11px] text-kumo-subtle"
        >
          <p>
            {finding.specialist} ·{" "}
            {finding.workspaceId && finding.path
              ? `${finding.workspaceId}/${finding.path}`
              : "Change-wide"}
          </p>
          <p className="font-mono">{finding.findingId}</p>
          {finding.detail && <p className="text-xs leading-5">{finding.detail}</p>}
        </div>
      )}
    </li>
  );
}

function FindingsSection({
  findings = [],
  state,
  onNavigate
}: {
  findings?: readonly ReviewFindingItem[];
  state: ReviewDeepLinkState;
  onNavigate: (state: ReviewDeepLinkState) => void;
}) {
  if (findings.length === 0) return <EmptySection>No findings are recorded for this candidate.</EmptySection>;
  const selectedIndex = findings.findIndex(
    (finding) => state.findingId === finding.findingId
  );
  return (
    <ul className="space-y-2" role="tree" aria-label="Review findings">
      {findings.map((finding, index) => {
        const selected = state.findingId === finding.findingId;
        return (
          <FindingTreeItem
            key={finding.findingId}
            finding={finding}
            index={index}
            size={findings.length}
            selected={selected}
            tabStop={selected || (selectedIndex < 0 && index === 0)}
            state={state}
            onNavigate={onNavigate}
          />
        );
      })}
    </ul>
  );
}

function ChecksSection({
  checks = [],
  onRetryCheck,
  readOnly
}: {
  checks?: readonly ReviewCheckItem[];
  onRetryCheck?: (checkId: string) => void;
  readOnly: boolean;
}) {
  if (checks.length === 0) return <EmptySection>No checks are scheduled for this candidate.</EmptySection>;
  return (
    <ul className="divide-y divide-kumo-line rounded border border-kumo-line">
      {checks.map((check) => (
        <li key={check.checkId} className="p-3 text-xs">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <details className="min-w-0 flex-1">
              <summary className="cursor-pointer font-medium text-kumo-default">
                {check.label}{" "}
                <span className="font-normal text-kumo-subtle">
                  · <StatusText status={check.state} />
                  {check.durationMs === undefined
                    ? ""
                    : ` · ${check.durationMs} ms`}
                </span>
              </summary>
              <div className="mt-2 border-t border-kumo-line pt-2 text-kumo-subtle">
                <p className="font-mono">{check.checkId}</p>
                {check.detail && <p className="mt-1 leading-5">{check.detail}</p>}
              </div>
            </details>
            {check.retryable && onRetryCheck && !readOnly && (
              <button type="button" onClick={() => onRetryCheck(check.checkId)} className="rounded border border-kumo-line px-2 py-1 text-kumo-default">
                Retry check
              </button>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}

function ReviewsSection({
  specialists = [],
  onRetrySpecialist,
  readOnly
}: {
  specialists?: readonly ReviewSpecialistItem[];
  onRetrySpecialist?: (reviewId: string) => void;
  readOnly: boolean;
}) {
  if (specialists.length === 0) {
    return <EmptySection>No specialist coverage is recorded for this candidate.</EmptySection>;
  }
  const completed = specialists.filter(
    (specialist) => specialist.state === "completed"
  ).length;
  const required = specialists.filter((specialist) => specialist.required);
  const completedRequired = required.filter(
    (specialist) => specialist.state === "completed"
  ).length;
  return (
    <div className="space-y-3">
      <p className="text-xs text-kumo-subtle">
        {completed}/{specialists.length} specialists completed ·{" "}
        {completedRequired}/{required.length} required coverage complete
      </p>
      <ul
        aria-label="Specialist coverage matrix"
        className="divide-y divide-kumo-line rounded border border-kumo-line"
      >
        {specialists.map((specialist) => (
          <li key={specialist.reviewId} className="p-3 text-xs">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <details className="min-w-0 flex-1">
                <summary className="cursor-pointer font-medium text-kumo-default">
                  {specialist.role}{" "}
                  <span className="font-normal text-kumo-subtle">
                    · {specialist.required ? "required" : "advisory"} ·{" "}
                    <StatusText status={specialist.state} /> · attempt{" "}
                    {specialist.attempt}
                  </span>
                </summary>
                <div className="mt-2 space-y-2 border-t border-kumo-line pt-2 text-kumo-subtle">
                  {specialist.latestMilestone && (
                    <p>Latest durable milestone: {specialist.latestMilestone}</p>
                  )}
                  {specialist.partialFindingCount !== undefined && (
                    <p>
                      Partial findings retained: {specialist.partialFindingCount}
                    </p>
                  )}
                  <p>
                    Domains: {specialist.domains.length > 0
                      ? specialist.domains.join(", ")
                      : "change-wide"}
                  </p>
                  <p>
                    Files: {specialist.files.length > 0
                      ? specialist.files.join(", ")
                      : "change-wide"}
                  </p>
                  <p className="font-mono">{specialist.reviewId}</p>
                  {specialist.parentReviewId && (
                    <p className="font-mono">
                      Retry of {specialist.parentReviewId}
                    </p>
                  )}
                </div>
              </details>
              {specialist.retryable && onRetrySpecialist && !readOnly && (
                <button
                  type="button"
                  onClick={() => onRetrySpecialist(specialist.reviewId)}
                  className="rounded border border-kumo-line px-2 py-1 text-kumo-default"
                >
                  Retry specialist
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ActivitySection({ activity = [] }: { activity?: readonly ReviewActivityItem[] }) {
  if (activity.length === 0) return <EmptySection>No durable activity is recorded yet.</EmptySection>;
  return (
    <ol
      className="space-y-3 border-l border-kumo-line pl-4"
      aria-label="Durable review activity"
    >
      {[...activity]
        .sort(
          (left, right) =>
            left.sequence - right.sequence ||
            (left.eventId < right.eventId ? -1 : left.eventId > right.eventId ? 1 : 0)
        )
        .map((event) => {
          const finiteTimestamp =
            typeof event.createdAt === "number" &&
            Number.isFinite(event.createdAt);
          const date = finiteTimestamp ? new Date(event.createdAt) : undefined;
          const hasTimestamp =
            date !== undefined && Number.isFinite(date.getTime());
          return (
        <li key={event.eventId} className="relative text-xs">
          <span className="absolute -left-[1.22rem] top-1 h-2 w-2 rounded-full bg-kumo-brand" aria-hidden="true" />
          <p className="font-medium text-kumo-default">{event.label}</p>
          {event.detail && (
            <details className="mt-1 text-kumo-subtle">
              <summary className="cursor-pointer">Show milestone detail</summary>
              <p className="mt-1 leading-5">{event.detail}</p>
            </details>
          )}
          <time
            className="mt-1 block text-[11px] text-kumo-subtle"
            dateTime={hasTimestamp ? date.toISOString() : undefined}
          >
            #{event.sequence} ·{" "}
            {hasTimestamp ? date.toLocaleString() : "Timestamp unavailable"}
          </time>
        </li>
          );
        })}
    </ol>
  );
}

function candidateForRoute(
  state: ReviewDeepLinkState,
  data: ReviewWorkbenchData
): ReviewCandidatePresentation | undefined {
  try {
    const candidate = reviewCandidatePresentation(data.candidate);
    const integrity = reviewRouteIntegrity({
      routeChangeId: state.changeId,
      routeCandidateId: state.candidateId,
      dataChangeId: data.changeId,
      dataCandidateId: candidate.candidateId
    });
    return integrity.status === "match" ? candidate : undefined;
  } catch {
    return undefined;
  }
}

export function ReviewWorkbench({
  state,
  onNavigate,
  data,
  onGateAction,
  onNavigateHunk,
  onMarkFileReviewed,
  onRetryCheck,
  onRetrySpecialist
}: ReviewWorkbenchProps) {
  const workbenchId = useId();
  const workbenchRef = useRef<HTMLElement>(null);
  const diffEditorRef = useRef<MonacoEditor.IStandaloneDiffEditor | null>(null);
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const [diffMode, setDiffMode] = useState<"unified" | "side-by-side">(
    "unified"
  );
  const candidate = data === undefined ? undefined : candidateForRoute(state, data);
  const routeMismatch = data !== undefined && candidate === undefined;
  const visibleData = routeMismatch ? undefined : data;
  const historical = candidate?.state === "superseded";
  const milestoneAnnouncement = reviewMilestoneAnnouncement({
    activity: visibleData?.activity,
    specialists: visibleData?.specialists
  });
  const conflictCount = (visibleData?.files ?? []).reduce(
    (total, file) => total + (file.conflictCount ?? 0),
    0
  );
  const failedCheckCount = (visibleData?.checks ?? []).filter((check) =>
    ["failed", "error", "timed-out"].includes(check.state)
  ).length;
  const assertiveAnnouncement = reviewAssertiveAnnouncement({
    candidateState: candidate?.state,
    freshness: visibleData?.summary?.freshness,
    conflictCount,
    gateEnabled: visibleData?.summary?.gate.enabled,
    blockerCount: visibleData?.summary?.blockers.length,
    failedCheckCount
  });
  const findingIds = (visibleData?.findings ?? []).map(
    (finding) => finding.findingId
  );
  let normalizedFiles: readonly ReviewChangedFileInput[] = [];
  let filePayloadRejected = false;
  try {
    normalizedFiles = groupReviewChangedFiles(
      visibleData?.files ?? []
    ).attention.flatMap((group) => group.files);
  } catch {
    filePayloadRejected = true;
  }
  const selectedFile = normalizedFiles.find(
    (file) =>
      file.workspaceId === state.workspaceId && file.path === state.file
  );
  const summary = visibleData?.summary;
  let selectedDiff: ReviewFileDiff | undefined;
  let diffRejected = false;
  if (visibleData?.diff !== undefined) {
    if (candidate === undefined || summary === undefined || selectedFile === undefined) {
      diffRejected = true;
    } else {
      try {
        selectedDiff = reviewFileDiffForSelection({
          candidateId: candidate.candidateId,
          baseCommitId: summary.baseCommitId,
          candidateCommitId: summary.candidateCommitId,
          candidateTreeId: summary.candidateTreeId,
          workspaceId: selectedFile.workspaceId,
          path: selectedFile.path,
          ...(selectedFile.previousPath === undefined
            ? {}
            : { previousPath: selectedFile.previousPath }),
          binary: selectedFile.binary === true,
          diff: visibleData.diff
        });
      } catch {
        diffRejected = true;
      }
    }
  }

  function focusActiveReviewSection(): void {
    workbenchRef.current
      ?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
      ?.focus();
  }

  function navigateFinding(direction: 1 | -1): void {
    const findingId = adjacentReviewIdentity(
      findingIds,
      state.findingId,
      direction
    );
    if (findingId === undefined) return;
    onNavigate({
      ...reviewStateForSection(state, "findings"),
      findingId: findingId as ReviewDeepLinkState["findingId"]
    });
  }

  const commands: readonly ReviewCommandItem[] = REVIEW_COMMAND_DEFINITIONS.map(
    (definition) => {
      if ("section" in definition) {
        return {
          definition,
          enabled: true,
          run: () =>
            onNavigate(reviewStateForSection(state, definition.section))
        };
      }
      switch (definition.id) {
        case "hunk.next":
        case "hunk.previous": {
          const enabled =
            state.section === "changes" &&
            state.file !== undefined &&
            (onNavigateHunk !== undefined || selectedDiff !== undefined);
          return {
            definition,
            enabled,
            ...(enabled
              ? {}
              : {
                  unavailableReason:
                    state.file === undefined
                      ? "Select a changed file first."
                      : state.section !== "changes"
                        ? "Open Changes to navigate diff hunks."
                        : "An exact immutable diff is not loaded yet."
                }),
            run: () => {
              const direction =
                definition.id === "hunk.next" ? "next" : "previous";
              if (onNavigateHunk) onNavigateHunk(direction);
              else diffEditorRef.current?.goToDiff(direction);
            }
          };
        }
        case "finding.next":
        case "finding.previous": {
          const enabled = findingIds.length > 0;
          return {
            definition,
            enabled,
            ...(enabled
              ? {}
              : { unavailableReason: "No findings are available for this candidate." }),
            run: () =>
              navigateFinding(definition.id === "finding.next" ? 1 : -1)
          };
        }
        case "focus.return":
          return {
            definition,
            enabled: true,
            run: focusActiveReviewSection,
            runAfterClose: true
          };
        case "file.mark-reviewed": {
          const enabled =
            historical !== true &&
            selectedFile !== undefined &&
            selectedFile.reviewed !== true &&
            onMarkFileReviewed !== undefined;
          const unavailableReason = historical
            ? "Historical candidates are read-only."
            : selectedFile === undefined
              ? "Select a changed file first."
              : selectedFile.reviewed
                ? "The selected file is already reviewed."
                : "File checkpoint updates are not connected yet.";
          return {
            definition,
            enabled,
            ...(enabled ? {} : { unavailableReason }),
            run: () => {
              if (selectedFile) {
                onMarkFileReviewed?.({
                  workspaceId: selectedFile.workspaceId,
                  path: selectedFile.path
                });
              }
            }
          };
        }
        case "gate.submit": {
          const enabled =
            historical !== true &&
            summary?.freshness === "current" &&
            summary.gate.enabled &&
            onGateAction !== undefined;
          const unavailableReason = historical
            ? "Historical candidates cannot be submitted."
            : summary === undefined
              ? "Candidate gate state is not available yet."
              : summary.freshness !== "current"
                ? "Restore candidate freshness before submission."
                : !summary.gate.enabled
                  ? summary.gate.reason ?? "The publication gate is blocked."
                  : "The publication action is not connected yet.";
          return {
            definition,
            enabled,
            ...(enabled ? {} : { unavailableReason }),
            run: () => onGateAction?.()
          };
        }
      }
    }
  );

  // Publish review commands to the global palette while this surface is
  // mounted, so ⌘K reaches them without mounting a second palette.
  const commandsRef = useRef(commands);
  commandsRef.current = commands;
  const commandSignature = commands
    .map(
      (command) =>
        `${command.definition.id}:${command.enabled ? 1 : 0}:${command.unavailableReason ?? ""}`
    )
    .join("|");
  const publishedCommands = useMemo(() => toSurfaceCommands(commandsRef.current).map((command) => ({
      ...command,
      // Read the run closure through the ref: it is rebuilt every render, and
      // re-registering that often would churn the registry for no gain.
      run: () => {
        const latest = commandsRef.current.find(
          (item) => item.definition.id === command.id
        );
        if (latest?.enabled) latest.run();
      }
    })), [commandSignature]);
  useSurfaceCommands("review-workbench", publishedCommands);

  function runCommand(commandId: ReviewCommandId): boolean {
    const command = commands.find(
      (candidateCommand) => candidateCommand.definition.id === commandId
    );
    if (!command?.enabled) return false;
    command.run();
    return true;
  }
  return (
    <section
      ref={workbenchRef}
      aria-label="Change review workbench"
      aria-keyshortcuts={REVIEW_WORKBENCH_ARIA_KEY_SHORTCUTS}
      onKeyDown={(event) => {
        const command = reviewCommandFromShortcut({
          key: event.key,
          scopeActive: true,
          editable: reviewShortcutTargetIsEditable(event.target),
          altKey: event.altKey,
          ctrlKey: event.ctrlKey,
          metaKey: event.metaKey,
          shiftKey: event.shiftKey,
          repeat: event.repeat,
          composing: event.nativeEvent.isComposing
        });
        if (command === undefined) return;
        if (command === "palette.open") {
          event.preventDefault();
          setCommandPaletteOpen(true);
          return;
        }
        if (!runCommand(command)) return;
        event.preventDefault();
      }}
      className="flex h-full min-h-0 flex-col bg-kumo-base"
    >
      {milestoneAnnouncement && (
        <div
          key={milestoneAnnouncement.key}
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {milestoneAnnouncement.message}
        </div>
      )}
      {assertiveAnnouncement && (
        <div role="alert" aria-atomic="true" className="sr-only">
          {assertiveAnnouncement}
        </div>
      )}
      <header className="sticky top-0 z-10 shrink-0 border-b border-kumo-line bg-kumo-base px-4 pt-3">
        <div className="flex flex-wrap items-start justify-between gap-3 pb-3">
          <div className="min-w-0">
            <h1 className="text-sm font-semibold text-kumo-default">Change review</h1>
            <p className="mt-1 truncate font-mono text-[11px] text-kumo-subtle">
              {state.changeId ?? "No Change"} · {state.candidateId ?? "Select a candidate"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded border border-kumo-line px-2 py-1 text-[11px] text-kumo-subtle">
              {state.comparison.replaceAll("-", " ")}
            </span>
            <ReviewCommandPalette
              open={commandPaletteOpen}
              onOpenChange={setCommandPaletteOpen}
              commands={commands}
              trigger={
                <button
                  type="button"
                  aria-keyshortcuts={REVIEW_PALETTE_ARIA_KEY_SHORTCUTS.join(" ")}
                  aria-haspopup="dialog"
                  title="Review commands (Ctrl/⌘+Shift+P)"
                  className="rounded border border-kumo-line px-2 py-1 text-[11px] text-kumo-subtle hover:bg-kumo-elevated hover:text-kumo-default"
                >
                  Commands
                </button>
              }
            />
          </div>
        </div>
        {visibleData?.summary && (
          <div
            aria-label="Candidate review status"
            className="flex flex-wrap gap-x-4 gap-y-1 pb-3 text-[11px] text-kumo-subtle"
          >
            <StatusText
              status={visibleData.summary.freshness}
              label={`Freshness: ${visibleData.summary.freshness.replaceAll("-", " ")}`}
            />
            <StatusText
              status={
                visibleData.summary.blockers.length > 0 ? "blocked" : "passed"
              }
              label={`Blockers: ${visibleData.summary.blockers.length}`}
            />
            <span>
              Base: <code>{visibleData.summary.baseCommitId}</code>
            </span>
            <span>
              Target: <code>{visibleData.summary.targetRef}</code>@
              {visibleData.summary.targetRevision} ·{" "}
              <code>{visibleData.summary.targetCommitId}</code>
            </span>
            <StatusText
              status={
                visibleData.summary.requiredChecks.passed ===
                visibleData.summary.requiredChecks.total
                  ? "passed"
                  : "pending"
              }
              label={`Required checks: ${visibleData.summary.requiredChecks.passed}/${visibleData.summary.requiredChecks.total}`}
            />
            <StatusText
              status={
                visibleData.summary.coverage.completedSpecialists ===
                visibleData.summary.coverage.requiredSpecialists
                  ? "completed"
                  : "pending"
              }
              label={`Specialist coverage: ${visibleData.summary.coverage.completedSpecialists}/${visibleData.summary.coverage.requiredSpecialists}`}
            />
          </div>
        )}
        <div
          role="tablist"
          aria-label="Review sections"
          aria-orientation="horizontal"
          className="flex min-w-0 gap-1 overflow-x-auto"
        >
          {SECTIONS.map((section, index) => (
            <button
              key={section.id}
              id={`${workbenchId}-tab-${section.id}`}
              type="button"
              role="tab"
              aria-selected={state.section === section.id}
              aria-controls={`${workbenchId}-panel`}
              tabIndex={state.section === section.id ? 0 : -1}
              onClick={() => onNavigate(reviewStateForSection(state, section.id))}
              onKeyDown={(event) => {
                if (event.altKey || event.ctrlKey || event.metaKey) return;
                const next = reviewRovingIndex(
                  event.key,
                  index,
                  SECTIONS.length,
                  "horizontal"
                );
                if (next === undefined) return;
                event.preventDefault();
                const tabs =
                  event.currentTarget.parentElement?.querySelectorAll<HTMLElement>(
                    '[role="tab"]'
                  );
                tabs?.[next]?.focus();
                onNavigate(reviewStateForSection(state, SECTIONS[next]!.id));
              }}
              className={`shrink-0 border-b-2 px-2 py-2 text-xs ${
                state.section === section.id
                  ? "border-kumo-brand text-kumo-default"
                  : "border-transparent text-kumo-subtle hover:text-kumo-default"
              }`}
            >
              {section.label}
            </button>
          ))}
        </div>
      </header>

      {routeMismatch && (
        <div role="alert" className="border-b border-red-500/30 bg-red-500/10 px-4 py-3 text-xs text-kumo-default">
          Review data does not match the immutable Change and candidate in this
          route. Nothing from the mismatched payload is shown.
        </div>
      )}

      {filePayloadRejected && (
        <div role="alert" className="border-b border-red-500/30 bg-red-500/10 px-4 py-3 text-xs text-kumo-default">
          Changed-file data is malformed and has been hidden.
        </div>
      )}

      {candidate && (
        <CandidateStatus candidate={candidate} state={state} onNavigate={onNavigate} />
      )}

      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div
          id={`${workbenchId}-panel`}
          role="tabpanel"
          aria-labelledby={`${workbenchId}-tab-${state.section}`}
          tabIndex={0}
          className="mx-auto max-w-6xl rounded border border-kumo-line bg-kumo-elevated p-4"
        >
          {state.section === "summary" && (
            <SummarySection
              summary={visibleData?.summary}
              state={state}
              onNavigate={onNavigate}
              onGateAction={onGateAction}
              historical={historical}
            />
          )}
          {state.section === "changes" && (
            <ChangesSection
              files={normalizedFiles}
              state={state}
              onNavigate={onNavigate}
              candidateId={candidate?.candidateId}
              diff={selectedDiff}
              diffRejected={diffRejected}
              diffMode={diffMode}
              onDiffModeChange={setDiffMode}
              onDiffMount={(editor) => {
                diffEditorRef.current = editor;
              }}
            />
          )}
          {state.section === "conflicts" && (
            <ConflictsSection files={normalizedFiles} state={state} onNavigate={onNavigate} />
          )}
          {state.section === "findings" && (
            <FindingsSection
              findings={visibleData?.findings}
              state={state}
              onNavigate={onNavigate}
            />
          )}
          {state.section === "checks" && (
            <ChecksSection
              checks={visibleData?.checks}
              onRetryCheck={onRetryCheck}
              readOnly={historical}
            />
          )}
          {state.section === "reviews" && (
            <ReviewsSection
              specialists={visibleData?.specialists}
              onRetrySpecialist={onRetrySpecialist}
              readOnly={historical}
            />
          )}
          {state.section === "activity" && <ActivitySection activity={visibleData?.activity} />}
        </div>
      </div>
    </section>
  );
}
